/**
 * PINS — promoted navigation destinations (#2362).
 *
 * A pin is not a record card, breadcrumb or second shelf. It is an offered
 * destination promoted into the navbar's first Pinned section. `scope:
 * 'navigation'` distinguishes the contract from historical stored record/view
 * values, which are ignored rather than replayed.
 *
 * ── Server-side, per user ──────────────────────────────────────────────────
 * A workspace that vanishes when you open the other laptop is not a workspace.
 * So this rides `core.user_setting` through `my_settings()` /
 * `set_user_setting()`, the same store as the column widths, the split width and
 * the relationship-picker frequency. No migration: a per-user key/value store is
 * what it is for.
 *
 * This paragraph used to continue *"`recordRecent` (crm/experience.ts) is
 * localStorage, and that is right for RECENTS — a machine-local trace of where
 * you have been"*, drawing a distinction between the two collections. **That is
 * no longer true and the distinction is retired** (#2325, and #2350's design
 * says to correct the comment rather than leave it): recents are also per-user
 * and server-backed, at `core.user_setting['recents']`, read and written by
 * `recents.ts` through this same pair of doors and sharing this hook's
 * `['user-settings']` query so the two cost one round trip. The device-local
 * reading was a reasonable one and it was wrong for a product where the same
 * browser serves more than one person — `recents.ts`'s header carries the
 * measurement.
 *
 * Console pins are navigation destinations only. Portal uses the same shared
 * storage shape under its own key for the same reason.
 */
import { useCallback, useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { MAX_PINS, pinsFromSettings, togglePin, type PinnedItem } from '@solum/ui'
import { useSolum } from '../data/client'

/* The SHAPE, the ceiling and the two reducers moved to
 * `packages/ui/src/shell/pins.ts` (#1466 F-30C ruling 2) when the portal gained
 * pins of its own. What stayed here is the DOOR — `useSolum`, the query keys,
 * the never-write-over-an-unread-base rule — because nothing in `packages/ui`
 * may fetch. Re-exported rather than re-declared so this app's importers do not
 * all have to move on the same day, and so there is exactly one definition. */
export { MAX_PINS, type PinnedItem }

/* The console's own key. The portal writes `portal.pins`: `core.user_setting` is
 * keyed by (user_id, key), `to` is a ROUTE here and a page SLUG there, and one
 * person can hold both a staff session and a portal seat. See pins.ts. */
const KEY = 'pins'

export interface Pins {
  items: PinnedItem[]
  isPinned: (to: string) => boolean
  toggle: (item: PinnedItem) => void
  remove: (to: string) => void
  /** the settings read has settled — before this, `items` is empty because it is UNKNOWN */
  ready: boolean
  busy: boolean
}

export function usePins(): Pins {
  const { client } = useSolum()
  const qc = useQueryClient()

  const q = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => client.raw().rpcScalar<Record<string, unknown>>('my_settings'),
    staleTime: 60_000,
  })
  // A refused settings read is settled, not pending: no pins are shown and the
  // panel says so, which is a degraded surface rather than a false claim that
  // the user has pinned nothing.
  const settled = !q.isLoading || q.isError

  const items = useMemo(() => pinsFromSettings(q.data, KEY), [q.data])

  const save = useMutation({
    mutationFn: (next: PinnedItem[]) =>
      client.raw().rpcScalar('set_user_setting', { p_key: KEY, p_value: next }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['user-settings'] }) },
  })

  /* Never write over an UNREAD list: `q.isSuccess` is the only state in which the
     base is known, and writing `[one pin]` over an unread map would erase every
     pin the user has. Skipping the toggle loses one click; the alternative loses
     the workspace. (The same rule the relationship-frequency bump follows.) */
  const write = useCallback((next: PinnedItem[]) => {
    if (!q.isSuccess) return
    save.mutate(next.slice(-MAX_PINS))
  }, [q.isSuccess, save])


  return {
    items,
    ready: settled,
    busy: save.isPending,
    isPinned: useCallback((to: string) => items.some((p) => p.to === to), [items]),
    toggle: useCallback((item: PinnedItem) => { write(togglePin(items, item)) }, [items, write]),
    remove: useCallback((to: string) => write(items.filter((p) => p.to !== to)), [items, write]),
  }
}
